<template>

    <b-table :fields="fields" :items="tableItems" :foot-clone="!readonly" details-td-class="coa-details">
        <template #cell(lot)="data">
            <b-input size="sm" :value="data.value" @input="handleInput({ lot: $event }, data.index)"
                @change="lookupSiblingCoa(data.index)" :readonly="readonly" />
        </template>
        <template #cell(expiry)="data">
            <b-input size="sm" type="date" :value="data.value" @input="handleInput({ expiry: $event }, data.index)"
                :readonly="readonly" />
        </template>
        <template #cell(cost)="data">
            <b-input size="sm" type="number" step="0.01" :value="data.value" @input="handleInput({ cost: $event }, data.index)"
                :readonly="readonly" placeholder="Cost" />
        </template>
        <template #cell(dateReceived)="data">
            <b-input size="sm" type="date" :value="data.value" @input="handleInput({ dateReceived: $event }, data.index)"
                :readonly="readonly" />
        </template>
        <template #cell(quantity)="data">
            <b-input size="sm" type="number" :value="data.value" @input="handleInput({ quantity: $event }, data.index)"
                :readonly="readonly" />
        </template>
        <template #cell(actions)="data">
            <template v-if="data.index !== tableItems.length - 1">
                <b-button variant="link" @click.prevent="removeItem(data.index)" title="Remove" tabindex="-1">
                    <b-icon icon="x-circle-fill" variant="danger" />
                </b-button>
            </template>
        </template>

        <!-- Certificate of Analysis for the lot on the line above; only rendered once the feature is on
             and the line has been started, so the blank entry line stays a single row. -->
        <template #row-details="row">
            <CoaFieldsComponent compact :readonly="readonly" :assay-percent="row.item.assayPercent"
                :assay-basis="row.item.assayBasis" :lod-percent="row.item.lodPercent"
                :bounds="activityFactorSettings.bounds" :decimal-places="activityFactorSettings.decimalPlaces"
                :sibling="siblingFor(row.item)" :sibling-prefilled="isPrefilledFromSibling(row.item)"
                :fixed-factor-usages="fixedFactorUsages"
                @update:assayPercent="handleInput({ assayPercent: $event }, row.index)"
                @update:assayBasis="handleInput({ assayBasis: $event }, row.index)"
                @update:lodPercent="handleInput({ lodPercent: $event }, row.index)" />
        </template>

        <template #foot(quantity)>
            <b-badge v-if="totalQuantity" :variant="quantityRemainingBadgeVariant">
                Remaining: {{ quantityRemaining }}
            </b-badge>
            <div v-else></div>
        </template>
        <template #foot()>
            <div></div>
        </template>
    </b-table>

</template>

<script>
import CoaFieldsComponent from '@/components/Inventory/CoaFieldsComponent.vue';
import { ACTIVITY_FACTOR_SETTINGS_OFF, fetchLotCoaSibling } from '@/util/activityFactorApi';
import { hasAnyCoaValue } from '@/util/activityFactor';

export default {
    name: "InventoryLotEntryComponent",
    components: { CoaFieldsComponent },
    props: {
        value: Array,
        totalQuantity: Number,
        readonly: Boolean,
        // Activity-factor configuration; COA entry appears on each lot line only when it is enabled.
        activityFactorSettings: { type: Object, default: () => ACTIVITY_FACTOR_SETTINGS_OFF },
        // Identity of the drug being received, for the same-lot COA lookup.
        drugSource: Number,
        packageId: Number,
        // Formulas in which this drug is a flagged ingredient with a fixed factor (COA recorded, not applied).
        fixedFactorUsages: { type: Array, default: () => [] }
    },
    data() {
        return {
            // Sibling COA rows keyed by lot number, so a lookup survives lines being added or removed.
            siblingsByLot: {},
            prefilledLots: {}
        };
    },
    computed: {
        showCoa() {
            return !!this.activityFactorSettings?.enabled;
        },
        fields() {
            return [
                { key: "lot", label: "Lot Number" },
                { key: "expiry", label: "Expiration" },
                { key: "cost", label: "Cost", class: "cost-col" },
                { key: "dateReceived", label: "Date Received" },
                { key: "quantity", label: "Quantity", class: "qty-col" },
                { key: "actions", label: "", class: "actions-col", hidden: this.readonly }
            ].filter(f => f.hidden !== true)
        },
        tableItems() {
            // _showDetails is b-table's own switch for the row-details slot; it is set on display
            // copies only, so the lots emitted back to the host never carry it.
            const rows = this.showCoa
                ? this.value.map(item => ({ ...item, _showDetails: true }))
                : this.value;
            if (this.readonly) return rows;
            return [
                ...rows,
                { }
            ]
        },
        totalEnteredQuantity() {
            return this.value.map(i => Number(i.quantity ?? 0)).reduce((p, a) => p + a, 0);
        },
        quantityRemaining() {
            return this.totalQuantity - this.totalEnteredQuantity;
        },
        quantityRemainingBadgeVariant() {
            if (this.quantityRemaining > 0) return "warning";
            if (this.quantityRemaining < 0) return "danger";
            return "success";
        }
    },
    emits: ["input"],
    methods: {
        handleInput(newData, index) {
            const newItems = [
                ...this.value
            ];

            newItems[index] = { ...newItems[index], ...newData };

            if (this.itemIsEmpty(newItems[index])) {
                newItems.splice(index, 1);
            }

            this.$emit('input', newItems);
        },
        itemIsEmpty(item) {
            if (item.lot) return false;
            if (item.expiry) return false;
            if (item.cost) return false;
            if (item.dateReceived) return false;
            if (item.quantity) return false;
            // A line started by typing COA first is a real line too.
            if (hasAnyCoaValue(item)) return false;
            return true;
        },
        removeItem(index) {
            const newItems = [
                ...this.value
            ];

            newItems.splice(index, 1);

            this.$emit('input', newItems);
        },
        lotKey(item) {
            return (item?.lot ?? '').trim();
        },
        siblingFor(item) {
            return this.siblingsByLot[this.lotKey(item)] ?? null;
        },
        isPrefilledFromSibling(item) {
            return !!this.prefilledLots[this.lotKey(item)];
        },
        /**
         * Looks up another inventory row for the same drug and lot number that already carries COA.
         * A line whose COA is still blank takes the sibling's values (and says so); a line that
         * already has values only gets the mismatch warning.
         */
        async lookupSiblingCoa(index) {
            if (!this.showCoa) return;
            const item = this.value[index];
            const lot = this.lotKey(item);
            if (!lot || !this.packageId) return;

            const sibling = await fetchLotCoaSibling(this.drugSource, this.packageId, lot);
            this.$set(this.siblingsByLot, lot, sibling);
            if (!sibling) return;

            // The line may have moved or been retyped while the request was in flight.
            const currentIndex = this.value.findIndex(i => this.lotKey(i) === lot);
            if (currentIndex < 0 || hasAnyCoaValue(this.value[currentIndex])) return;

            this.$set(this.prefilledLots, lot, true);
            this.handleInput({
                assayPercent: sibling.assayPercent,
                assayBasis: sibling.assayBasis,
                lodPercent: sibling.lodPercent
            }, currentIndex);
        }
    }
}

</script>

<style scoped>
::v-deep(th) {
    padding: 0 !important;
    padding-right: 6px !important;
    font-size: .9em !important;
    font-weight: normal !important;
}

::v-deep(td) {
    padding: 6px 0 !important;
    padding-right: 6px !important;
}

::v-deep(td:last-child) {
    padding-right: 0 !important;
}

::v-deep(td.coa-details) {
    padding: 0 0 10px 12px !important;
    border-top: none;
}

::v-deep(.cost-col) {
    width: 120px;
}

::v-deep(.qty-col) {
    width: 200px;
}

::v-deep(.actions-col) {
    width: 40px;
}

::v-deep(.badge) {
    margin-top: 6px;
    font-size: .95em;
    display: block;
}

::v-deep(input[readonly]) {
    background: white;
    color: black;
}
</style>
